// http://res.plazaro.loc/clyxut/cs/get-database-data?hotelId=clyxut&arrival=2018-03-19+00%3A00%3A00&departure=2018-03-20+00%3A00%3A00&stayType=vacation&all=0
import queryString from 'query-string';
import moment from 'moment';

import { DATE_FORMAT } from './constants';
import { jsonToUrlEncoded, isEventPackageFeatureEnabled, SANDAMIANO_HOTEL_ID } from './utils';
import BedConfig from './enums/BedConfig';
import { isWithoutAccommodation } from './apiHelpers';
import { getDiscountCodeResponse, isDiscountCodeValid } from "./store/discountCodeSelector";

const dateTimeFormat = 'YYYY-MM-DD HH:mm:ss';

export async function fetchInitData(state) {
	const { hotelId, lang, theme, apiOrigin, packageId } = state.app;
	const params = { theme };

	if (packageId && isEventPackageFeatureEnabled(hotelId)) {
		params.packageId = packageId;
	}

	try {
		const response = await fetch(`${apiOrigin}/${hotelId}/${lang}/data?${queryString.stringify(params)}`);
		return await response.json();

	} catch (e) {
		throw(e);
	}
}

export async function fetchRoomTypesAvailabilitiesData(state, from, to) {
	const { hotelId, lang, apiOrigin } = state.app;

	const params = {
		from: from.format(dateTimeFormat),
		to: to.format(dateTimeFormat),
	};

	const response = await fetch(`${apiOrigin}/${hotelId}/${lang}/availabilities?${queryString.stringify(params)}`);
	return await response.json();
}

export async function fetchReservationData(state, arrival = null, departure = null) {
	const { hotelId, lang, apiOrigin, all } = state.app;
	const formValues = state.form.bookingForm.values;

	if (!arrival) arrival = formValues.arrival;
	if (!departure) departure = formValues.departure;

	const params = {
		arrival: moment(arrival, DATE_FORMAT).format(dateTimeFormat),
		departure: moment(departure, DATE_FORMAT).format(dateTimeFormat),
		stayType: formValues.stayType,
		all,
	};

	const response = await fetch(`${apiOrigin}/${hotelId}/${lang}/rooms?${queryString.stringify(params)}`);
	return await response.json();
}

export async function fetchAresData(state) {
	const { hotelId, lang, apiOrigin } = state.app;
	const formValues = state.form.bookingForm.values;

	const params = {
		do: 'getAresData',
		ico: formValues.companyIc || '',
	};

	const response = await fetch(`${apiOrigin}/${hotelId}/${lang}/?${queryString.stringify(params)}`, {
		method: 'POST',
	});

	return await response.json();
}

export async function fetchDiscountCode(state){
	const { hotelId, lang, apiOrigin } = state.app;
	const formValues = state.form.bookingForm.values;

	const params = {
		discountCode: formValues.discountCode || '',
	};

	const response = await fetch(`${apiOrigin}/${hotelId}/${lang}/discount-code?${queryString.stringify(params)}`);
	return await response.json();
}

export async function validatePhone(state, phone) {
	const { apiOrigin } = state.app;

	// řešíme přes API endpoint, tudíž musíme jít přes jinou subdoménu
	const apiBaseOrigin = apiOrigin.replace('//res.', '//www.');

	// Do API posíláme číslo bez mezer (v inputu je uživatel zadat může).
	const params = { number: (phone || '').replace(/\s/g, '') };

	const response = await fetch(`${apiBaseOrigin}/api/v1/phone-number/validate?${queryString.stringify(params)}`);
	return await response.json();
}

export async function submitReservation(state, recaptchaToken) {
	const { hotelId, lang, apiOrigin, advancePaymentPercentage, isAdvancePaymentRequired  } = state.app;
	const formValues = state.form.bookingForm.values;
	const allRoomTypesIds = Object.keys(state.app.apiRoomsResponse.roomTypes).map(id => id.startsWith('_') ? id : parseInt(id, 10));

	let roomCount = 0;
	let roomTypeIndex = 1;
	let roomType = {};

	// rezervace pouze kalendářové události bez ubytování - neodesílají se pokoje ani detaily hostů
	const withoutAccommodation = isWithoutAccommodation(formValues);

	// TODO vyhodnocovat nějak jinak než podle hotelId?
	const hasGuestDetails = hotelId === SANDAMIANO_HOTEL_ID && !withoutAccommodation;
	const guestDetails = Array.from({ length: state.app.totalNoOfGuests }, (_, i) => (formValues.guestDetails || [])[i] || {});
	let guestDetailsOffset = 0;

	for (let roomTypeId in formValues.rooms) {
		if (!formValues.rooms.hasOwnProperty(roomTypeId) || withoutAccommodation) continue;

		const rooms = formValues.rooms[roomTypeId];

		roomTypeId = roomTypeId.startsWith('_') ? roomTypeId : parseInt(roomTypeId, 10);
		for (let room of rooms) {
			roomType[roomTypeIndex] = {};
			roomType[roomTypeIndex]['guest'] = room.guestTypes;
			roomType[roomTypeIndex]['item'] = {};

			if (hasGuestDetails) {
				const roomGuestCount = Object.values(room.guestTypes).reduce((sum, count) => sum + count, 0);
				roomType[roomTypeIndex]['guestDetails'] = guestDetails
					.slice(guestDetailsOffset, guestDetailsOffset + roomGuestCount)
					.map((guestDetail, i) => ({
						...guestDetail,
						diet_restriction_id: (formValues.dietRestriction || [])[guestDetailsOffset + i] || null,
					}));
				guestDetailsOffset += roomGuestCount;
			}

			for (let allRoomTypesId of allRoomTypesIds) {
				roomType[roomTypeIndex]['item'][allRoomTypesId] = {};
				roomType[roomTypeIndex]['item'][allRoomTypesId]['count'] = allRoomTypesId === roomTypeId ? 1 : 0;
				roomType[roomTypeIndex]['item'][allRoomTypesId]['doubleBed'] = BedConfig.getApiValue(room.bedConfig);
				roomType[roomTypeIndex]['item'][allRoomTypesId]['extraFees'] = room.extraFees !== undefined
					? Object.entries(room.extraFees).filter(([_, selected]) => selected).map(([extraFeeId]) => extraFeeId)
					: [];
				roomType[roomTypeIndex]['item'][allRoomTypesId]['package'] = (allRoomTypesId === roomTypeId && room.package) ? room.package : null;
			}

			roomCount++;
			roomTypeIndex++;
		}
	}

	// sandamiano: primární kontakt (jméno, adresa) se přebírá z prvního hosta, protože se samostatně nevyplňuje
	const primaryGuestDetails = hasGuestDetails ? (guestDetails[0] || {}) : {};

	const body = {
		arrival: formValues.arrival,
		departure: formValues.departure,
		roomtype: roomType,
		roomcount: roomCount,
		stayType: formValues.stayType,
		boardBase: formValues.boardBase,
		firstname: (hasGuestDetails ? primaryGuestDetails.firstname : formValues.firstName) || '',
		surname: (hasGuestDetails ? primaryGuestDetails.surname : formValues.lastName) || '',
		email: formValues.email || '',
		phone: (formValues.phone || '').replace(/\s/g, ''),
		street: (hasGuestDetails ? primaryGuestDetails.street : formValues.street) || '',
		city: (hasGuestDetails ? primaryGuestDetails.city : formValues.city) || '',
		zip: (hasGuestDetails ? primaryGuestDetails.zip : formValues.postCode) || '',
		note: formValues.note || '',
		company: formValues.isCompany ? 'on' : null,
		company_ino: formValues.companyIc || '',
		company_name: formValues.companyName || '',
		company_vatin: formValues.companyDic || '',
		company_street: formValues.companyStreet || '',
		company_zip: formValues.companyPostCode || '',
		company_city: formValues.companyCity || '',
		company_country_id: formValues.companyCountry || '',
		terms: formValues.agree ? 'on' : '',
		stornoInsurance: formValues.stornoInsurance && !withoutAccommodation ? 'on' : '',
		hotelId,
		token: recaptchaToken,
	};

	if (withoutAccommodation) {
		// pseudo-pokoj obsahuje vybraný balíček a počty hostů dle typu
		const eventRoom = Object.values(formValues.rooms)[0][0];
		body.withoutAccommodation = 1;
		body.package = eventRoom.package;
		body.guest = eventRoom.guestTypes;
		body.dietRestriction = formValues.dietRestriction;
	}

	if (formValues.paymentMethod) {
		body.paymentInstrument = formValues.paymentMethod.toLowerCase();
		if (isAdvancePaymentRequired || (advancePaymentPercentage < 100 && formValues.advancePaymentOnly)) {
			body.advancePaymentPercentage = advancePaymentPercentage;
		}
	}

	if (isDiscountCodeValid(state)) {
		body.discountCode = getDiscountCodeResponse(state).discountCode;
	}

	const response = await fetch(`${apiOrigin}/${hotelId}/${lang}/submit-reservation?version=2`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
		},
		body: jsonToUrlEncoded(body)
	});

	return await response.json();
}

function arrayBufferToBase64(buffer) {
	var binary = '';
	var bytes = [].slice.call(new Uint8Array(buffer));

	bytes.forEach((b) => binary += String.fromCharCode(b));

	return window.btoa(binary);
};

export async function fetchImage(state, url) {
	const response = await fetch(url);
	const buffer = await response.arrayBuffer();

	const base64Flag = 'data:image/jpeg;base64,';
	const imageStr = arrayBufferToBase64(buffer);

	return base64Flag + imageStr;
}


// WEBPACK FOOTER //
// ./src/api.js